import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampus } from '../context/CampusContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { 
  ArrowLeft, FileText, Calendar, Trash2, Eye, Copy, Download,
  Sparkles, Shield, Database, Leaf, Wine, Trophy, Heart,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const iconMap = {
  cybersecurity: Shield,
  bigdata: Database,
  agrotech: Leaf,
  wine: Wine,
  sports: Trophy,
  health: Heart
};

const HistoryPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { getCampusById } = useCampus();
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedArticle, setExpandedArticle] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API_URL}/articles`, {
        headers: getAuthHeaders()
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Error al cargar los artículos');
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (articleId) => {
    try {
      await axios.delete(`${API_URL}/articles/${articleId}`, {
        headers: getAuthHeaders()
      });
      setArticles(prev => prev.filter(a => a.id !== articleId));
      toast.success('Artículo eliminado');
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Error al eliminar el artículo');
    }
  };

  const copyToClipboard = (article) => {
    navigator.clipboard.writeText(article.full_content);
    toast.success('Copiado al portapapeles');
  };

  const downloadMarkdown = (article) => {
    const content = `# ${article.title}\n\n${article.full_content}`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${article.title.replace(/\s+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Descargando archivo...');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (articleId) => {
    setExpandedArticle(prev => prev === articleId ? null : articleId);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Historial de Artículos</h1>
              <p className="text-xs text-slate-500">Todos tus contenidos generados</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Skeleton className="w-12 h-12 rounded-xl" />
                    <div className="flex-1 space-y-3">
                      <Skeleton className="h-5 w-2/3" />
                      <Skeleton className="h-4 w-1/3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No hay artículos aún
              </h3>
              <p className="text-slate-500 mb-6">
                Crea tu primer artículo para verlo aquí
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-first-article-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Crear artículo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="articles-list">
            {articles.map((article) => {
              const campus = getCampusById(article.campus);
              const CampusIcon = iconMap[article.campus] || FileText;
              const isExpanded = expandedArticle === article.id;

              return (
                <Card 
                  key={article.id} 
                  className="border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid={`article-card-${article.id}`}
                >
                  <CardContent className="p-0">
                    {/* Header row */}
                    <div 
                      className="p-6 flex items-start gap-4 cursor-pointer"
                      onClick={() => toggleExpand(article.id)}
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ 
                          backgroundColor: campus?.color + '20' || '#e2e8f0',
                          color: campus?.color || '#64748b'
                        }}
                      >
                        <CampusIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {article.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(article.created_at)}
                          </span>
                          <span>·</span>
                          <span>{campus?.name || article.campus}</span>
                          <span>·</span>
                          <span>{article.content_type}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(article); }}
                          data-testid={`copy-btn-${article.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); downloadMarkdown(article); }}
                          data-testid={`download-btn-${article.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`delete-btn-${article.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar artículo?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. El artículo será eliminado permanentemente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteArticle(article.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="px-6 pb-6 border-t border-slate-100">
                        <div className="pt-6">
                          {/* Index */}
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-slate-700 mb-3">Índice</h4>
                            <div className="flex flex-wrap gap-2">
                              {article.index.map((item, idx) => (
                                <span 
                                  key={idx}
                                  className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs"
                                >
                                  {idx + 1}. {item.title}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Content preview */}
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-3">Contenido</h4>
                            <ScrollArea className="h-64 rounded-lg border border-slate-200 p-4">
                              <div className="prose prose-sm prose-slate max-w-none">
                                {article.full_content.split('\n').map((line, idx) => {
                                  if (line.startsWith('## ')) {
                                    return <h2 key={idx} className="text-lg font-semibold mt-4 mb-2">{line.replace('## ', '')}</h2>;
                                  }
                                  if (line.startsWith('---')) {
                                    return <hr key={idx} className="my-4" />;
                                  }
                                  if (line.trim()) {
                                    return <p key={idx} className="text-slate-600 mb-2">{line}</p>;
                                  }
                                  return null;
                                })}
                              </div>
                            </ScrollArea>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
